import React from "react";

/*
 * BAND 3 — the breakdown tables.
 *
 * "By source" comes first and is the highest-value object on the page: v1
 * treated referral, organic and LinkedIn as one undifferentiated stream of
 * leads, so the fact that referral converts several times better than organic
 * was not merely hard to see, it was not represented anywhere.
 *
 * BORDERED ROWS, NOT CARDS. Each row is a comparison against the rows above and
 * below it; a card puts a gutter and a shadow between things whose whole purpose
 * is to be read against each other. Numbers are monospaced for the same reason —
 * digits have to line up by column to be scanned.
 *
 * Both tables end with a coverage line rather than a total. On this dataset the
 * coverage IS the finding: lead_source is unattributed on the largest single
 * bucket, and status_reason is recorded on 5.5% of lost companies. A table that
 * printed only what it knew would read as a complete account of why deals are
 * lost, which is the most expensive kind of wrong a report can be.
 */

const SOURCE_LABELS = {
  linkedin: "LinkedIn",
  referral: "Referral",
  organic: "Organic",
  unattributed: "Unattributed",
};

const REASON_LABELS = {
  went_quiet: "Went quiet",
  ghosted: "Ghosted",
  chose_competitor: "Chose a competitor",
  no_budget: "No budget",
  bad_fit: "Bad fit",
  ignored: "Ignored",
  junk: "Junk",
  "(none recorded)": "No reason recorded",
};

const rate = (value, suppressed) => (suppressed || value == null ? "n/a" : `${value.toFixed(1)}%`);

/* Coverage notes come from the metric layer unpunctuated, because the CLI dump
   prints them on their own line. Here they are followed by another sentence, and
   without this the two ran together mid-word. */
const sentence = (text) => (!text ? "" : /[.!?]$/.test(text.trim()) ? text.trim() : `${text.trim()}.`);

function BySource({ bySource, selectedSource, onSelectSource }) {
  const { rows, totals, coverage } = bySource;

  return (
    <div className="crm-brk">
      <div className="crm-brk__head">
        <span className="crm-brk__title">By source</span>
        <span className="crm-brk__sub">
          {selectedSource ? "click the row again to clear" : "click a row to filter the page"}
        </span>
      </div>

      <div className="crm-brk__cols">
        <span className="crm-brk__c-name">Source</span>
        <span className="crm-brk__c-n">Leads</span>
        <span className="crm-brk__c-n">Calls</span>
        <span className="crm-brk__c-n">Opps</span>
        <span className="crm-brk__c-n">Won</span>
        <span className="crm-brk__c-rate">Win rate</span>
      </div>

      {rows.map((row) => (
        /* The whole row is the control, matching how a row opens a record
           everywhere else in this app. A button rather than a div so it is
           reachable by keyboard — this is the page filter, not a hover effect. */
        <button
          type="button"
          key={row.source}
          aria-pressed={selectedSource === row.source}
          onClick={() => onSelectSource(row.source)}
          className={
            "crm-brk__row crm-brk__row--click" +
            (row.source === "unattributed" ? " is-unattributed" : "") +
            (selectedSource === row.source ? " is-selected" : "")
          }
        >
          <span className="crm-brk__c-name">{SOURCE_LABELS[row.source] || row.source}</span>
          <span className="crm-brk__c-n crm-num">{row.leads}</span>
          <span className="crm-brk__c-n crm-num">{row.calls}</span>
          <span className="crm-brk__c-n crm-num">{row.opps}</span>
          <span className="crm-brk__c-n crm-num">{row.won}</span>
          <span
            className={`crm-brk__c-rate crm-num${row.win_rate_suppressed ? " is-muted" : ""}`}
            title={row.win_rate_suppressed ? `${row.won} of ${row.opps} opportunities — base too small for a rate` : ""}
          >
            {rate(row.win_rate, row.win_rate_suppressed)}
          </span>
        </button>
      ))}

      <div className="crm-brk__row crm-brk__row--total">
        <span className="crm-brk__c-name">All sources</span>
        <span className="crm-brk__c-n crm-num">{totals.leads}</span>
        <span className="crm-brk__c-n crm-num">{totals.calls}</span>
        <span className="crm-brk__c-n crm-num">{totals.opps}</span>
        <span className="crm-brk__c-n crm-num">{totals.won}</span>
        <span className="crm-brk__c-rate crm-num">{rate(totals.win_rate, totals.win_rate_suppressed)}</span>
      </div>

      <div className="crm-brk__foot">
        Source attributed on {coverage.recorded} of {coverage.total} companies
        {coverage.pct == null ? "" : ` (${coverage.pct}%)`}. {sentence(coverage.note)}
        {/* Without this, five rows of "n/a" read as a broken table rather than
            as a real statement about how few deals get priced in a quarter. */}
        {rows.length > 0 && rows.every((row) => row.win_rate_suppressed) && (
          <>
            {" "}No per-source win rate: every source closed fewer than{" "}
            {rows[0].rates.opp_to_won.min_base} opportunities in this window, and the
            totals row is the only base big enough to divide.
          </>
        )}
      </div>
    </div>
  );
}

function LostReasons({ lost }) {
  const { rows, total, coverage } = lost;
  // Bars scale to the largest reason, not to the total — the question a reader
  // asks of this table is which reason dominates the others.
  const max = Math.max(1, ...rows.map((r) => r.n));

  return (
    <div className="crm-brk">
      <div className="crm-brk__head">
        <span className="crm-brk__title">Why deals closed out</span>
        <span className="crm-brk__sub">{total} in this window</span>
      </div>

      {!rows.length && (
        <div className="crm-brk__foot">Nothing closed out in this window.</div>
      )}

      {rows.map((row) => (
        <div key={row.reason} className={`crm-brk__row${row.reason === "(none recorded)" ? " is-unattributed" : ""}`}>
          <span className="crm-brk__c-reason">{REASON_LABELS[row.reason] || row.reason}</span>
          <span className="crm-brk__barwrap">
            <span className="crm-brk__bar" style={{ width: `${Math.round((row.n / max) * 100)}%` }} />
          </span>
          <span className="crm-brk__c-count crm-num">{row.n}</span>
          <span className="crm-brk__c-pct crm-num">{row.pct == null ? "—" : `${Math.round(row.pct)}%`}</span>
        </div>
      ))}

      <div className="crm-brk__foot">
        Reason recorded on {coverage.recorded} of {coverage.total}
        {coverage.pct == null ? "" : ` (${coverage.pct}%)`}. {sentence(coverage.note)}
      </div>
    </div>
  );
}

export default function ReportsBreakdowns({ bySource, lost, selectedSource, onSelectSource }) {
  return (
    <section className="crm-brk-band">
      <div className="crm-funnel__head">
        <span className="crm-funnel__eyebrow">Breakdowns</span>
        <span className="crm-funnel__caption">same cohort window as the funnel</span>
      </div>
      <div className="crm-brk-grid">
        <BySource bySource={bySource} selectedSource={selectedSource} onSelectSource={onSelectSource} />
        <LostReasons lost={lost} />
      </div>
    </section>
  );
}
